// Google Analytics 4 + Consent Mode v2.
// Measurement IDs are public, safe to ship in client code.
export const GA_MEASUREMENT_ID = "G-WPS98XBRRS";
export const CLARITY_PROJECT_ID = "x0qt1lt8zg";
const STORAGE_KEY = "simpletech-cookie-consent-v1";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

// gtag.js is injected on demand by loadGA() during browser idle time so the
// ~155 KiB GTM bundle never blocks LCP. Consent Mode v2 defaults are still
// set inline in the document <head> (see __root.tsx) before any tracking can
// fire, so deferring the script is safe.
let gaLoaded = false;
let clarityLoaded = false;


export function hasAnalyticsConsent(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { analytics?: boolean };
    return !!parsed.analytics;
  } catch {
    return false;
  }
}

function hasMarketingConsent(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { marketing?: boolean };
    return !!parsed.marketing;
  } catch {
    return false;
  }
}

/**
 * Inject gtag.js lazily. Called from requestIdleCallback so the GTM bundle
 * never sits on the LCP critical path.
 */
export function loadGA() {
  if (gaLoaded || typeof window === "undefined") return;
  gaLoaded = true;
  const s = document.createElement("script");
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  s.async = true;
  document.head.appendChild(s);
  window.gtag?.("js", new Date());
  // send_page_view: false — trackPageview() sends the manual page_view for both
  // the initial load and SPA route changes. Without this, config auto-sends one
  // too and every visit is double-counted in GA4.
  window.gtag?.("config", GA_MEASUREMENT_ID, { send_page_view: false });
}


/**
 * Update Consent Mode v2 state. Call from the cookie banner after the
 * user makes a choice.
 */
export function updateConsent(analytics: boolean, marketing: boolean) {
  if (typeof window === "undefined") return;
  // Make sure gtag is bootstrapped so the update is delivered.
  if (!gaLoaded) loadGA();
  window.gtag?.("consent", "update", {
    ad_storage: marketing ? "granted" : "denied",
    ad_user_data: marketing ? "granted" : "denied",
    ad_personalization: marketing ? "granted" : "denied",
    analytics_storage: analytics ? "granted" : "denied",
  });
  if (analytics) loadClarity();
}

// Only run Clarity on the real production domain so preview/lovable hosts
// don't pollute the dashboard with sessions from the wrong URL.
const CLARITY_ALLOWED_HOSTS = ["simpletechhelper.com", "www.simpletechhelper.com"];

export async function loadClarity() {
  if (clarityLoaded || typeof window === "undefined") return;
  if (!CLARITY_ALLOWED_HOSTS.includes(window.location.hostname)) return;
  clarityLoaded = true;

  const Clarity = (await import("@microsoft/clarity")).default;
  Clarity.init(CLARITY_PROJECT_ID);
}

export function trackPageview(path: string, title?: string) {
  if (!gaLoaded || typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_title: title ?? document.title,
    page_location: window.location.href,
  });
}


/* ------------------------------------------------------------------ *
 * Lead / interaction event tracking
 *
 * All events go through trackEvent(), which uses the same window.gtag
 * wrapper as trackPageview(). Consent Mode v2 gating is identical:
 * gtag('consent','default',...) is set inline in <head> before anything
 * loads, and updateConsent() upgrades it — so GA4 itself suppresses
 * storage/transmission for these events exactly as it does for page_view.
 * ------------------------------------------------------------------ */

export function trackEvent(name: string, params: Record<string, unknown> = {}) {
  if (!gaLoaded || typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", name, params);
}

/** Short label describing where in the page a link lives. */
function linkLocation(el: HTMLElement): string {
  const explicit = el.closest<HTMLElement>("[data-ga-location]");
  if (explicit?.dataset.gaLocation) return explicit.dataset.gaLocation;
  if (el.closest("[data-ga-location='sticky_bar']")) return "sticky_bar";
  if (el.closest("footer")) return "footer";
  if (el.closest("header")) return "header";
  if (el.closest("form")) return "contact_page";
  if (el.closest("section")?.matches("section:first-of-type")) return "hero";
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/contact")) {
    return "contact_page";
  }
  return "body";
}

// Dedupe: ignore a repeat of the same event+url within this window (ms).
const DEDUPE_MS = 1000;
const lastFired = new Map<string, number>();

function fireOnce(name: string, params: Record<string, unknown>) {
  const key = `${name}|${String(params.link_url ?? "")}`;
  const now = Date.now();
  const prev = lastFired.get(key);
  if (prev && now - prev < DEDUPE_MS) return;
  lastFired.set(key, now);
  trackEvent(name, params);
}

let clickTrackingBound = false;

/**
 * Delegated click listener on document — covers links rendered anywhere,
 * including the sticky mobile call/text/email bar and the footer.
 */
export function initClickTracking() {
  if (clickTrackingBound || typeof document === "undefined") return;
  clickTrackingBound = true;

  document.addEventListener(
    "click",
    (e) => {
      const target = e.target as HTMLElement | null;
      if (!target || typeof target.closest !== "function") return;

      // Explicit opt-in elements (e.g. the booking calendar CTA button).
      const tagged = target.closest<HTMLElement>("[data-ga-event]");
      if (tagged?.dataset.gaEvent) {
        fireOnce(tagged.dataset.gaEvent, {
          link_url: (tagged as HTMLAnchorElement).href ?? window.location.href,
          link_location: linkLocation(tagged),
        });
      }

      const anchor = target.closest<HTMLAnchorElement>("a[href]");
      if (!anchor) return;
      const href = anchor.getAttribute("href") ?? "";
      const params = { link_url: href, link_location: linkLocation(anchor) };

      if (href.startsWith("tel:")) fireOnce("click_to_call", params);
      else if (href.startsWith("sms:")) fireOnce("sms_click", params);
      else if (href.startsWith("mailto:")) fireOnce("email_click", params);
      else if (anchor.matches("[data-ga-booking]")) fireOnce("booking_click", params);
    },
    true
  );
}

/** Fire only after a contact form submission actually succeeds. */
export function trackGenerateLead(formName = "contact") {
  trackEvent("generate_lead", { form_name: formName });
}
